import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, Pause, Play, Archive, Crown, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface InviteOwnerResult {
  emp_code: string;
  pairing_code: string;
  tenant_code: string | null;
  expires_at: string;
}

interface WebLoginResult {
  email: string;
  emp_code: string;
  tenant_code: string | null;
  temp_password: string | null;
  reused_existing_password: boolean;
}

interface DetailResponse {
  tenant: {
    id: string;
    slug: string;
    tenant_code: string;
    company_name: string;
    status: 'pending_approval' | 'trial' | 'active' | 'suspended' | 'archived';
    plan: string | null;
    trial_ends_at: string | null;
    cutoff_day: number | null;
    timezone: string | null;
    created_at: string;
    updated_at: string;
  };
  employees: Array<{ emp_code: string; status: string; profile_completion_pct: number | null }>;
  users: Array<{
    id: string;
    emp_code: string;
    role: string;
    status: string;
    display_name: string | null;
    created_at: string;
  }>;
  recent_audit: Array<{
    id: string;
    timestamp: string;
    action: string;
    level: string;
    target_type: string | null;
    target_id: string | null;
  }>;
  subscriptions: Array<{
    product: string;
    status: string;
    current_period_end: string | null;
    trial_started_at: string | null;
    requested_at: string | null;
    suspend_reason: string | null;
  }>;
}

export function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['platform', 'tenant', id],
    queryFn: () => apiFetch<DetailResponse>(`/platform/tenants/${id}`),
    enabled: !!id,
  });

  const extendTrial = useMutation({
    mutationFn: (days: number) =>
      apiFetch(`/platform/tenants/${id}/extend-trial`, { method: 'POST', body: { days } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
  const setStatus = useMutation({
    mutationFn: (args: { status: string; reason?: string }) =>
      apiFetch(`/platform/tenants/${id}/status`, { method: 'POST', body: args }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
  });
  const setPlan = useMutation({
    mutationFn: (plan: string) =>
      apiFetch(`/platform/tenants/${id}/plan`, { method: 'POST', body: { plan } }),
    onSuccess: () => refetch(),
  });
  const approve = useMutation({
    mutationFn: () =>
      apiFetch(`/platform/tenants/${id}/approve`, { method: 'POST', body: {} }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
      qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      qc.invalidateQueries({ queryKey: ['platform', 'stats'] });
    },
  });
  const productAction = useMutation({
    mutationFn: (args: { product: string; action: string; body?: unknown }) =>
      apiFetch(`/platform/tenants/${id}/products/${args.product}/${args.action}`, {
        method: 'POST',
        body: args.body ?? {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] }),
  });
  const userAction = useMutation({
    mutationFn: (args: { userId: string; action: 'suspend' | 'activate'; reason?: string }) =>
      apiFetch(`/platform/users/${args.userId}/${args.action}`, {
        method: 'POST',
        body: args.reason ? { reason: args.reason } : {},
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] }),
  });
  const setOwner = useMutation({
    mutationFn: (body: { promote_user_id?: string; demote_user_id?: string; demote_to?: string }) =>
      apiFetch(`/platform/tenants/${id}/set-owner`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] }),
  });
  const inviteOwner = useMutation({
    mutationFn: (body: { first_name: string; last_name: string; email?: string }) =>
      apiFetch<InviteOwnerResult>(`/platform/tenants/${id}/invite-owner`, { method: 'POST', body }),
    onSuccess: (res) => {
      setInviteResult(res);
      qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
    },
  });
  const issueWebLogin = useMutation({
    mutationFn: (body: { email: string; first_name?: string; last_name?: string }) =>
      apiFetch<WebLoginResult>(`/platform/tenants/${id}/issue-web-login`, { method: 'POST', body }),
    onSuccess: (res) => {
      setWebLoginResult(res);
      qc.invalidateQueries({ queryKey: ['platform', 'tenant', id] });
    },
  });
  const [inviteResult, setInviteResult] = useState<InviteOwnerResult | null>(null);
  const [webLoginResult, setWebLoginResult] = useState<WebLoginResult | null>(null);

  if (isLoading || !data) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">กำลังโหลด…</div>;
  }
  const t = data.tenant;

  async function handleExtend() {
    const ans = prompt('ต่ออายุ trial กี่วัน? (1-365)', '30');
    const days = Number(ans);
    if (!Number.isInteger(days) || days < 1 || days > 365) return;
    await extendTrial.mutateAsync(days);
  }
  async function handleSuspend() {
    const reason = prompt('เหตุผลที่ระงับ (จะเก็บใน audit log)') ?? '';
    if (!confirm(`ยืนยันระงับ ${t.company_name}?`)) return;
    await setStatus.mutateAsync({ status: 'suspended', reason });
  }
  async function handleActivate() {
    await setStatus.mutateAsync({ status: 'active' });
  }
  async function handleArchive() {
    if (!confirm(`ARCHIVE ${t.company_name}? — ข้อมูลยังอยู่แต่ Owner จะ login ไม่ได้`)) return;
    await setStatus.mutateAsync({ status: 'archived' });
  }
  async function handleSetPlan() {
    const ans = prompt('เปลี่ยน plan เป็น (เช่น starter, pro, enterprise)', t.plan ?? 'starter');
    if (!ans || ans === t.plan) return;
    await setPlan.mutateAsync(ans);
  }
  async function handleApprove() {
    if (!confirm(`อนุมัติ ${t.company_name}? — trial จะเริ่มทันที (30 วัน)`)) return;
    await approve.mutateAsync();
  }
  const activeOwners = data.users.filter((u) => u.role === 'OWNER' && u.status === 'active');
  async function handlePromoteOwner(user: DetailResponse['users'][number]) {
    const name = user.display_name ?? user.emp_code;
    // ถ้ามีเจ้าของเดิมอยู่คนเดียว → ถามว่าจะ "เปลี่ยน" (ลดคนเดิม) หรือ "เพิ่มเป็นเจ้าของร่วม"
    const sole = activeOwners.length === 1 && activeOwners[0]!.id !== user.id ? activeOwners[0]! : null;
    let demote_user_id: string | undefined;
    if (sole) {
      const replace = confirm(
        `ตั้ง "${name}" เป็นเจ้าของกิจการ\n\n` +
          `ลดบทบาทเจ้าของเดิม "${sole.display_name ?? sole.emp_code}" เป็น HR ด้วยหรือไม่?\n\n` +
          `[ตกลง] = เปลี่ยนเจ้าของ (คนเดิมเป็น HR)\n[ยกเลิก] = เพิ่มเป็นเจ้าของร่วม (คนเดิมยังเป็นเจ้าของ)`,
      );
      if (replace) demote_user_id = sole.id;
    } else if (!confirm(`ตั้ง "${name}" เป็นเจ้าของกิจการ?`)) {
      return;
    }
    await setOwner.mutateAsync({ promote_user_id: user.id, demote_user_id });
  }
  async function handleDemoteOwner(user: DetailResponse['users'][number]) {
    const name = user.display_name ?? user.emp_code;
    if (!confirm(`ลดบทบาท "${name}" จากเจ้าของกิจการเป็น HR?`)) return;
    await setOwner.mutateAsync({ demote_user_id: user.id, demote_to: 'HR' });
  }
  async function handleInviteOwner() {
    const first = prompt('ชื่อเจ้าของตัวจริง')?.trim();
    if (!first) return;
    const last = prompt('นามสกุลเจ้าของ')?.trim();
    if (!last) return;
    const email = prompt('อีเมล (ไม่บังคับ — เว้นว่างได้)')?.trim() ?? '';
    setInviteResult(null);
    await inviteOwner.mutateAsync({ first_name: first, last_name: last, email });
  }
  async function handleIssueWebLogin() {
    const email = prompt('อีเมลเจ้าของ (สำหรับ login เว็บ ด้วย email+รหัสผ่าน)')?.trim();
    if (!email) return;
    const first = prompt('ชื่อ (เว้นว่างได้ ถ้ามีพนักงานอีเมลนี้อยู่แล้ว)')?.trim() || undefined;
    const last = first ? prompt('นามสกุล')?.trim() || undefined : undefined;
    setWebLoginResult(null);
    await issueWebLogin.mutateAsync({ email, first_name: first, last_name: last });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/tenants" className="p-1 hover:bg-gray-100 rounded">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">{t.company_name}</h1>
            <p className="text-xs text-gray-500">
              <span className="font-mono">{t.tenant_code}</span> · {t.status} · {t.plan ?? 'starter'}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Quick info */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
          <InfoTile label="พนักงาน active" value={data.employees.filter((e) => e.status === 'active' || e.status === 'probation').length} />
          <InfoTile label="ผู้ใช้ active" value={data.users.filter((u) => u.status === 'active').length} />
          <InfoTile label="Cutoff day" value={t.cutoff_day ?? '—'} />
          <InfoTile
            label="Trial หมดอายุ"
            value={t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString('th-TH') : '—'}
          />
        </div>

        {/* Approve banner — only when pending */}
        {t.status === 'pending_approval' && (
          <div className="card p-4 bg-amber-50 border-amber-300 border-2">
            <div className="flex items-start gap-3">
              <div className="text-3xl">⏳</div>
              <div className="flex-1">
                <h2 className="font-semibold text-amber-900">บริษัทรออนุมัติ</h2>
                <p className="text-xs text-amber-800 mt-0.5">
                  Owner เข้าระบบไม่ได้จนกว่าจะกดอนุมัติ — ระบบจะเริ่ม trial 30 วันทันที
                </p>
              </div>
            </div>
            <button
              onClick={handleApprove}
              disabled={approve.isPending}
              className="btn-primary w-full mt-3"
            >
              <CheckCircle2 className="w-5 h-5" />
              {approve.isPending ? 'กำลังอนุมัติ…' : 'อนุมัติเลย'}
            </button>
          </div>
        )}

        {/* Ops */}
        <div className="card p-4">
          <h2 className="font-semibold mb-2">การจัดการ</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <button onClick={handleExtend} disabled={extendTrial.isPending || t.status === 'pending_approval'} className="btn-secondary text-sm">
              <Clock className="w-4 h-4" /> ต่ออายุ Trial
            </button>
            <button onClick={handleSetPlan} disabled={setPlan.isPending} className="btn-secondary text-sm">
              <Crown className="w-4 h-4" /> เปลี่ยน Plan
            </button>
            {t.status === 'suspended' || t.status === 'archived' ? (
              <button onClick={handleActivate} disabled={setStatus.isPending} className="btn-primary text-sm">
                <Play className="w-4 h-4" /> Activate
              </button>
            ) : (
              <button
                onClick={handleSuspend}
                disabled={setStatus.isPending || t.status === 'pending_approval'}
                className="btn-secondary text-sm text-orange-700 border-orange-200"
              >
                <Pause className="w-4 h-4" /> Suspend
              </button>
            )}
            <button onClick={handleArchive} disabled={setStatus.isPending} className="btn-secondary text-sm text-red-700 border-red-200">
              <Archive className="w-4 h-4" /> Archive
            </button>
          </div>
        </div>

        {/* Product access (JobPilot / EvalPilot) */}
        <div className="card p-4">
          <h2 className="font-semibold mb-2">การเข้าถึงแอป</h2>
          <div className="space-y-2">
            {(['jobpilot', 'evalpilot'] as const).map((product) => {
              const sub = data.subscriptions.find((s) => s.product === product);
              const status = sub?.status ?? 'none';
              const label = product === 'jobpilot' ? 'JobPilot' : 'EvalPilot';
              const trialLeft =
                status === 'trial' && sub?.current_period_end
                  ? Math.max(0, Math.ceil((new Date(sub.current_period_end).getTime() - Date.now()) / 86_400_000))
                  : null;
              const busy = productAction.isPending;
              const doExtend = () => {
                const ans = prompt('ต่ออายุ trial กี่วัน? (1-365)', '14');
                const days = Number(ans);
                if (!Number.isInteger(days) || days < 1 || days > 365) return;
                productAction.mutate({ product, action: 'extend-trial', body: { days } });
              };
              const doSuspend = () => {
                const reason = prompt('เหตุผลที่ระงับแอปนี้ (เก็บใน audit)') ?? '';
                if (!confirm(`ระงับ ${label} ของบริษัทนี้?`)) return;
                productAction.mutate({ product, action: 'suspend', body: { reason } });
              };
              return (
                <div key={product} className="flex items-center justify-between gap-2 border-b border-gray-50 last:border-0 pb-2 last:pb-0">
                  <div className="min-w-0">
                    <span className="font-medium">{label}</span>
                    <span className={`badge ml-2 ${STATUS_CLASS[status] ?? 'bg-gray-100 text-gray-500'}`}>
                      {STATUS_LABEL[status] ?? status}
                    </span>
                    {trialLeft !== null && <span className="text-xs text-gray-500 ml-2">เหลือ {trialLeft} วัน</span>}
                  </div>
                  <div className="flex gap-1 flex-wrap justify-end">
                    {(status === 'requested' || status === 'expired' || status === 'cancelled' || status === 'rejected' || status === 'suspended' || status === 'none') && (
                      <button onClick={() => productAction.mutate({ product, action: 'approve' })} disabled={busy} className="btn-primary text-xs">
                        {status === 'requested' ? 'อนุมัติ · trial 14 วัน' : 'เปิด trial'}
                      </button>
                    )}
                    {status === 'trial' && (
                      <>
                        <button onClick={doExtend} disabled={busy} className="btn-secondary text-xs">ต่อ trial</button>
                        <button onClick={() => productAction.mutate({ product, action: 'activate' })} disabled={busy} className="btn-primary text-xs">เปิดถาวร</button>
                      </>
                    )}
                    {(status === 'trial' || status === 'active') && (
                      <button onClick={doSuspend} disabled={busy} className="btn-secondary text-xs text-orange-700 border-orange-200">ระงับ</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* เจ้าของกิจการ — โอน/เปลี่ยน/เชิญ (ใช้เมื่อคนที่สมัครไม่ใช่เจ้าของตัวจริง) */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-semibold flex items-center gap-1.5">
              <Crown className="w-4 h-4 text-amber-500" /> เจ้าของกิจการ
            </h2>
            <div className="flex gap-2">
              <button onClick={handleInviteOwner} disabled={inviteOwner.isPending} className="btn-secondary text-xs">
                {inviteOwner.isPending ? 'กำลังออกรหัส…' : 'เชิญ (รหัสจับคู่ LINE)'}
              </button>
              <button onClick={handleIssueWebLogin} disabled={issueWebLogin.isPending} className="btn-secondary text-xs">
                {issueWebLogin.isPending ? 'กำลังออกรหัส…' : 'ออกรหัสเข้าเว็บ (email)'}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            เจ้าของปัจจุบัน:{' '}
            {activeOwners.length > 0
              ? activeOwners.map((o) => o.display_name ?? o.emp_code).join(', ')
              : '— ยังไม่มี'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            คนที่สมัครไม่ใช่เจ้าของตัวจริง? ตั้งสมาชิกในรายการด้านล่างขึ้นเป็นเจ้าของ หรือเชิญเจ้าของใหม่ด้วยรหัสจับคู่ — ระบบกันไม่ให้เหลือ 0 เจ้าของ
          </p>
          {inviteResult && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-sm font-medium text-emerald-900">
                ออกรหัสเชิญเจ้าของแล้ว — ส่งให้เจ้าของไปจับคู่ในแอป PayPilot
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-gray-500">รหัสบริษัท</div>
                  <div className="font-mono text-lg text-gray-900">{inviteResult.tenant_code ?? '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">รหัสจับคู่</div>
                  <div className="font-mono text-lg text-gray-900">{inviteResult.pairing_code}</div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                หมดอายุ {new Date(inviteResult.expires_at).toLocaleString('th-TH')} · เจ้าของเปิดแอป → จับคู่ด้วยรหัส → ได้สิทธิ์เจ้าของทันที
              </p>
            </div>
          )}
          {webLoginResult && (
            <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
              <p className="text-sm font-medium text-indigo-900">
                ออกรหัสเข้าเว็บแล้ว — login ที่เว็บ PayPilot ด้วยอีเมล+รหัสนี้ (ไม่ต้องใช้ LINE) แล้วสลับบริษัทด้วย TenantSwitcher
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-gray-500">อีเมล</div>
                  <div className="font-mono text-sm text-gray-900 break-all">{webLoginResult.email}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">รหัสผ่านชั่วคราว</div>
                  <div className="font-mono text-lg text-gray-900">
                    {webLoginResult.reused_existing_password ? '(ใช้รหัสเดิมของอีเมลนี้)' : webLoginResult.temp_password}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {webLoginResult.reused_existing_password
                  ? 'อีเมลนี้มีรหัสเข้าเว็บอยู่แล้ว (เคยตั้งที่บริษัท/แอปอื่น) — ใช้รหัสเดิมได้เลย · ผูกบริษัทนี้ให้แล้ว'
                  : 'ส่งรหัสนี้ให้เจ้าของอย่างปลอดภัย · เปลี่ยนรหัสได้ในแอปภายหลัง'}
              </p>
            </div>
          )}
        </div>

        {/* Users */}
        <div className="card p-4">
          <h2 className="font-semibold mb-2">ผู้ใช้ ({data.users.length})</h2>
          <div className="space-y-1">
            {data.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0 gap-2">
                <div className="min-w-0 flex items-center gap-1.5">
                  {u.role === 'OWNER' && <Crown className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                  <span className="font-medium truncate">{u.display_name ?? u.emp_code}</span>
                  <span className="text-xs text-gray-500 shrink-0">{u.emp_code} · {u.role}</span>
                  {u.status === 'suspended' && <span className="badge bg-rose-100 text-rose-700 shrink-0">ถูกระงับ</span>}
                </div>
                <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                  {u.role !== 'OWNER' && u.status === 'active' && (
                    <button
                      onClick={() => handlePromoteOwner(u)}
                      disabled={setOwner.isPending}
                      className="btn-secondary text-xs"
                    >
                      ตั้งเป็นเจ้าของ
                    </button>
                  )}
                  {u.role === 'OWNER' && activeOwners.length > 1 && (
                    <button
                      onClick={() => handleDemoteOwner(u)}
                      disabled={setOwner.isPending}
                      className="btn-secondary text-xs text-orange-700 border-orange-200"
                    >
                      ลดเป็น HR
                    </button>
                  )}
                  {u.status === 'suspended' ? (
                    <button
                      onClick={() => userAction.mutate({ userId: u.id, action: 'activate' })}
                      disabled={userAction.isPending}
                      className="btn-secondary text-xs"
                    >
                      ปลดระงับ
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const reason = prompt('เหตุผลที่ระงับผู้ใช้รายนี้ (เก็บใน audit)') ?? '';
                        if (!confirm(`ระงับ ${u.display_name ?? u.emp_code}? — ผู้ใช้รายนี้จะเข้าระบบไม่ได้`)) return;
                        userAction.mutate({ userId: u.id, action: 'suspend', reason });
                      }}
                      disabled={userAction.isPending}
                      className="btn-secondary text-xs text-orange-700 border-orange-200"
                    >
                      ระงับ
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent audit */}
        <div className="card p-4">
          <h2 className="font-semibold mb-2">Audit ล่าสุด (20)</h2>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {data.recent_audit.map((a) => (
              <div key={a.id} className="text-xs flex items-baseline gap-2 py-1 border-b border-gray-50 last:border-0">
                <span className="text-gray-400 font-mono">
                  {new Date(a.timestamp).toLocaleString('th-TH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="font-mono">{a.action}</span>
                {a.target_type && <span className="text-gray-500">{a.target_type}:{a.target_id}</span>}
              </div>
            ))}
            {data.recent_audit.length === 0 && (
              <p className="text-xs text-gray-400 italic">ยังไม่มีกิจกรรม</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  none: 'ยังไม่ขอ',
  requested: 'รออนุมัติ',
  trial: 'ทดลอง',
  active: 'เปิดใช้งาน',
  expired: 'หมดอายุ',
  suspended: 'ถูกระงับ',
  cancelled: 'ยกเลิก',
  rejected: 'ปฏิเสธ',
};
const STATUS_CLASS: Record<string, string> = {
  none: 'bg-gray-100 text-gray-500',
  requested: 'bg-amber-100 text-amber-700',
  trial: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  expired: 'bg-rose-100 text-rose-700',
  suspended: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-gray-100 text-gray-500',
  rejected: 'bg-gray-100 text-gray-500',
};

function InfoTile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="card p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
